import type {
  CableAgentQuestion,
  CableExtractionGap,
  CableTrayGap,
} from "../../findCableGaps";
import type {
  BuildAgentQuestions,
  KarlBoardDecision,
  KarlQuestion,
} from "./types";

const text = (value: string | undefined, fallback = ""): string =>
  (value ?? "").trim() || fallback;

/**
 * A question the run left open about where cable comes from. These belong in
 * the sources phase: until one is answered the board is held out of the
 * general-source set, so its circuits have nowhere to originate.
 */
const fromSourceQuestions = (
  questions: CableAgentQuestion[],
): KarlQuestion[] =>
  questions
    .filter((question) => (question.candidates ?? []).length > 0)
    .map((question) => ({
      id: `source:${text(question.kind, "unknown")}:${(question.candidates ?? [])
        .slice()
        .sort()
        .join(",")}`,
      kind: "decide" as const,
      step: "sources" as const,
      why: text(question.why),
      subjects: (question.candidates ?? []).slice().sort(),
      suggested:
        question.suggested && typeof question.suggested === "object"
          ? question.suggested
          : undefined,
      devices: 0,
    }));

/**
 * Devices no schedule in the package can feed. Deliberately NOT a grouping
 * question: the estimator cannot draw a box that conjures a missing schedule,
 * and presenting it as work to do is how a documentation gap turns into an
 * afternoon wasted on the plan.
 */
const fromExtractionGaps = (gaps: CableExtractionGap[]): KarlQuestion[] =>
  gaps.map((gap) => ({
    id: `extraction:${text(gap.kind)}:${text(gap.system) || (gap.tags ?? []).length}`,
    kind: "supply" as const,
    step: "grouping" as const,
    why: text(gap.why),
    subjects: gap.boards_missing?.length
      ? gap.boards_missing.slice().sort()
      : (gap.tags ?? []).slice().sort(),
    devices: gap.devices ?? 0,
  }));

/** A tray the schematic leans on that the drawing barely carries. */
const fromTrayGaps = (gaps: CableTrayGap[]): KarlQuestion[] =>
  gaps.map((gap) => ({
    id: `tray:${text(gap.tray_type)}`,
    kind: "draw" as const,
    step: "grouping" as const,
    why: text(gap.why),
    subjects: [text(gap.tray_type)].filter(Boolean),
    devices: gap.devices_assigned ?? 0,
  }));

export const buildAgentQuestions: BuildAgentQuestions = (bundle) => {
  const empty = {
    questions: [],
    decisions: [],
    mainBoard: null,
    scheduleBoard: null,
    strandedByExtraction: 0,
  };
  if (!bundle) return empty;

  const provenance = bundle.provenance ?? {};
  const roles = provenance.board_roles ?? {};
  // Published in both places while the agent settles on one; read either.
  const extraction =
    provenance.extraction_gaps ?? bundle.circuit_gaps ?? [];

  const questions = [
    ...fromSourceQuestions(provenance.source_questions ?? []),
    ...fromExtractionGaps(extraction),
    ...fromTrayGaps(provenance.tray_gaps ?? []),
  ];

  const decidedBy = roles.decided_by ?? {};
  const excluded = new Set(roles.excluded ?? []);
  const decisions: KarlBoardDecision[] = Object.keys(decidedBy)
    .sort()
    .map((board) => ({
      board,
      decidedBy: decidedBy[board],
      system: roles.systems?.[board] ?? "",
      isMain: roles.main === board,
      isExcluded: excluded.has(board),
    }));

  return {
    // Sorted so the list does not reshuffle between renders of the same run.
    questions: questions.sort((a, b) => a.id.localeCompare(b.id)),
    decisions,
    mainBoard: roles.main ?? null,
    scheduleBoard: roles.schedule_board ?? null,
    strandedByExtraction: extraction.reduce(
      (total, gap) => total + (gap.devices ?? 0),
      0,
    ),
  };
};
